import { HttpErrorResponse } from '@angular/common/http';
import type { TranslocoService } from '@jsverse/transloco';
import type { ToastMessageOptions } from 'primeng/api';

/** Преводачът на Transloco (`translate` или `TranslocoService.translate`). */
export type Translate = (key: string, params?: Record<string, unknown>) => string;

/** `Translate` от услугата на Transloco. */
export function translatorOf(transloco: TranslocoService): Translate {
  return (key, params) => transloco.translate(key, params);
}

/**
 * Тостът на отказ, който екранът обяснява сам — заявката е с
 * `handlesOwnErrors()`, затова тостът е един (#407). `knownKey` е
 * съобщението на екрана за познатия код; без него тостът казва
 * `fallbackKey` и причината от API-то, която иначе се губи.
 */
export function refusalToast(
  err: unknown,
  knownKey: string | undefined,
  fallbackKey: string,
  t: Translate,
  params?: Record<string, unknown>,
): ToastMessageOptions {
  return knownKey
    ? { severity: 'error', summary: t(knownKey, params) }
    : { severity: 'error', summary: t(fallbackKey, params), detail: apiErrorReason(err, t) };
}

/**
 * Причината на провалена заявка, преведена: `error.api.<CODE>` →
 * `error.http.<status>` → `error.http.generic`. Параметрите на превода идват
 * от `details` на отказа, с датите като навсякъде в интерфейса.
 *
 * Общият тостер на `errorInterceptor` казва точно това. Екран, който пуска
 * заявката с `handlesOwnErrors()`, го ползва за кодовете, които не познава —
 * иначе причината на непознатия отказ се губи зад „Грешка при запис“ (#407).
 */
export function apiErrorReason(err: unknown, t: Translate): string {
  if (!(err instanceof HttpErrorResponse)) return t('error.http.generic');

  const body = err.error as { code?: unknown; details?: unknown } | null;
  const apiCode = typeof body?.code === 'string' ? body.code : null;
  // Отказът може да назове конкретния запис (напр. кое извлечение държи таксата, #286).
  const details = body?.details;
  const params = details && typeof details === 'object'
    ? withDisplayDates(details as Record<string, unknown>)
    : undefined;

  const keys = apiCode
    ? [`error.api.${apiCode}`, `error.http.${err.status}`, 'error.http.generic']
    : [`error.http.${err.status}`, 'error.http.generic'];

  for (const key of keys) {
    const translated = t(key, params);
    if (translated !== key) return translated; // Transloco връща самия ключ, когато липсва
  }
  return t('error.http.generic');
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Датите от API-то (`YYYY-MM-DD`) се показват като навсякъде в интерфейса — dd.MM.yyyy. */
function withDisplayDates(details: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(details).map(([key, value]) => {
      const m = typeof value === 'string' ? ISO_DATE.exec(value) : null;
      return [key, m ? `${m[3]}.${m[2]}.${m[1]}` : value];
    }),
  );
}
